import React, { useEffect, useState } from "react";
import { Loader2, Wand2, Download, Pencil, Save, Lightbulb, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";

const EditableText = ({ label, value, onSave, testid }) => {
  const [edit, setEdit] = useState(false);
  const [v, setV] = useState(value || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => setV(value || ""), [value]);
  const save = async () => { setSaving(true); try { await onSave(v); setEdit(false); } finally { setSaving(false); } };
  return (
    <div className="mb-3" data-testid={`${testid}-block`}>
      <div className="flex items-center justify-between mb-1">
        <span className="font-semibold text-sm">{label}</span>
        {!edit && <button onClick={() => setEdit(true)} className="text-xs text-primary inline-flex items-center gap-1 hover:underline" data-testid={`${testid}-edit-btn`}><Pencil className="w-3 h-3" /> تعديل</button>}
      </div>
      {edit ? (
        <div className="space-y-2">
          <Textarea value={v} onChange={(e) => setV(e.target.value)} rows={3} className="bg-secondary border-border text-sm" data-testid={`${testid}-input`} />
          <div className="flex gap-2">
            <Button size="sm" onClick={save} disabled={saving} className="bg-primary text-white gap-1 h-8 text-xs" data-testid={`${testid}-save-btn`}>{saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />} حفظ</Button>
            <Button size="sm" variant="ghost" onClick={() => { setEdit(false); setV(value || ""); }} className="h-8 text-xs gap-1"><X className="w-3 h-3" /> إلغاء</Button>
          </div>
        </div>
      ) : <p className="text-sm text-muted-foreground leading-relaxed" data-testid={`${testid}-text`}>{value || "—"}</p>}
    </div>
  );
};

export function LogoStudio({ projectId, vi, logo, setLogo, onIdentityChange, refresh }) {
  const [hint, setHint] = useState("");
  const [loading, setLoading] = useState(false);

  const saveField = (field) => async (val) => {
    try {
      const { data } = await api.put(`/projects/${projectId}/identity-text`, { [field]: val });
      onIdentityChange(data.visual_identity);
      toast.success("تم الحفظ — سيُبنى الشعار القادم على هذا الوصف");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); throw err; }
  };

  const gen = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/suggest-logo`, { hint });
      setLogo(data.logo);
      await refresh();
      toast.success("تم توليد الشعار من مفهومك وألوان هويتك ✅");
    } catch (err) { toast.error(formatError(err.response?.data?.detail) || "فشل توليد الشعار"); }
    finally { setLoading(false); }
  };

  return (
    <div data-testid="logo-studio">
      <EditableText label="مفهوم الشعار" value={vi.logo_concept} onSave={saveField("logo_concept")} testid="logo-concept" />
      <EditableText label="التموضع" value={vi.positioning} onSave={saveField("positioning")} testid="positioning" />
      <div className="mt-5 pt-5 border-t border-border space-y-3">
        <div className="flex items-center justify-between">
          <span className="font-semibold text-sm">الشعار المقترح</span>
          <Button size="sm" variant="outline" onClick={gen} disabled={loading} className="gap-2 border-primary/40 text-primary" data-testid="generate-logo-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
            {logo ? "إعادة توليد" : "توليد شعار بالذكاء الاصطناعي"}
          </Button>
        </div>
        <div className="relative">
          <Lightbulb className="w-4 h-4 text-accent absolute end-3 top-1/2 -translate-y-1/2" />
          <Input value={hint} onChange={(e) => setHint(e.target.value)} maxLength={300}
            placeholder="تلميحك للشعار (اختياري): مثال «حرف ر على شكل سهم صاعد» أو «فنجان قهوة عصري»"
            className="bg-secondary border-border pe-9 text-sm" data-testid="logo-hint-input" />
        </div>
        <p className="text-[11px] text-muted-foreground">يُبنى الشعار من مفهوم الشعار والتموضع والشعار النصي وألوان الهوية فقط، مع تلميحك إن وُجد.</p>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> يتم تصميم الشعار…</div>
        ) : logo ? (
          <div className="flex items-center gap-4">
            <img src={logo} alt="logo" className="w-28 h-28 rounded-xl object-contain border border-border bg-white" data-testid="generated-logo-preview" />
            <a href={logo} download="logo.png"><Button size="sm" variant="ghost" className="gap-2 text-muted-foreground"><Download className="w-4 h-4" /> تحميل</Button></a>
          </div>
        ) : null}
      </div>
    </div>
  );
}
